/**
 * StartHere.tsx · 09_APP · OS-006 · capability C35 (Today page) with C15 (gifts), migration v51
 *
 * The Start here card, to the design Emily approved 2026-10-15
 * (00_BRAIN/PROPOSALS/TODAY_PAGE_v2_2026-10-15.html): supporters who have not heard from her
 * lately, newest gift first, each with a short note drafted in her voice. She reads, she sends.
 *
 * ⛔ NOTHING IS SENT FROM HERE. "Open in Gmail" opens a draft in her own Gmail and she presses Send
 * there (hard rule 12). "I sent it" only records the contact, and if she edited the note, keeps the
 * difference as a voice lesson (draft_diff).
 * Note wording is Claude's draft until she approves it: 00_BRAIN/PROPOSALS/CHECKIN_NOTES_2026-10-16.md
 */

import { useEffect, useState } from "react";
import { os, OsError, type CheckinList } from "../lib/os";
import { draftNote, gaveLine, gmailComposeUrl, type CheckinPerson } from "../lib/checkin_notes";

type Mode = "closed" | "notes" | "who";

function todayKey() {
  return "c2c-starthere-hidden-" + new Date().toLocaleDateString("en-CA");
}
function hiddenToday(): boolean {
  try { return localStorage.getItem(todayKey()) === "1"; } catch { return false; }
}
function hideToday() {
  try { localStorage.setItem(todayKey(), "1"); } catch { /* private window: hides for this visit only */ }
}

export function StartHere({ say }: { say: (m: string) => void }) {
  const [data, setData] = useState<CheckinList | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [mode, setMode] = useState<Mode>("closed");
  const [hidden, setHidden] = useState(hiddenToday());
  const [open, setOpen] = useState<Record<string, boolean>>({});
  const [text, setText] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState<string | null>(null);

  useEffect(() => {
    let live = true;
    os.checkinList(12)
      .then((d) => live && setData(d))
      .catch((e) => live && setErr(e instanceof OsError ? e.message : String(e)));
    return () => { live = false; };
  }, []);

  if (hidden) {
    return (
      <div className="start start-quiet">
        <div className="lbl">Start here</div>
        <div className="d">Held for today. <button className="linkbtn" onClick={() => setHidden(false)}>Show it anyway</button></div>
      </div>
    );
  }

  const people = data?.people ?? [];
  const total = data?.due_total ?? 0;
  const noteFor = (p: CheckinPerson) => text[p.id] ?? draftNote(p);

  async function sent(p: CheckinPerson) {
    setBusy(p.id);
    try {
      const draft = draftNote(p);
      const r = await os.checkinSent(p.id, draft, noteFor(p));
      setData((d) => d && { ...d, due_total: d.due_total - 1, people: d.people.filter((x) => x.id !== p.id) });
      say(r.learned_from_edit
        ? `Logged. Your changes were saved so future drafts sound more like you.`
        : `Logged. ${p.first_name || "They"} moves off this list.`);
    } catch (e) {
      say("Couldn't log that: " + (e instanceof OsError ? e.message : String(e)));
    } finally { setBusy(null); }
  }

  async function copy(p: CheckinPerson) {
    try { await navigator.clipboard.writeText(noteFor(p)); say("Note copied."); }
    catch { say("Couldn't copy. Select the text in the box instead."); }
  }

  let headline = "Checking who hasn't heard from you lately…";
  if (err) headline = "Couldn't load your supporters.";
  else if (data && total === 0) headline = "Everyone who gave has heard from you lately.";
  else if (data) headline = `${total} of your supporters haven't heard from you in a while.`;

  return (
    <section className="start" aria-label="Start here">
      <div className="lbl">Start here</div>
      <div className="h">{headline}</div>
      {err && <div className="refusebox" style={{ marginTop: 8 }}>The database refused, or could not be reached: {err}</div>}
      {data && total > 0 && (
        <div className="d">
          Newest gift first. A short note to each is drafted in your voice. You read, you send.
        </div>
      )}

      {data && total > 0 && (
        <div className="acts">
          <button className="btn go" onClick={() => setMode(mode === "notes" ? "closed" : "notes")}>
            {mode === "notes" ? "Close" : "Read the notes"}
          </button>
          <button className="btn ghost" onClick={() => setMode(mode === "who" ? "closed" : "who")}>
            {mode === "who" ? "Close" : "See who"}
          </button>
          <button className="btn plain" onClick={() => { hideToday(); setHidden(true); }}>Not today</button>
        </div>
      )}

      {mode !== "closed" && people.length > 0 && (
        <ol className="ci-list">
          {people.map((p) => {
            const showNote = mode === "notes" || open[p.id];
            return (
              <li key={p.id} className="ci">
                <div className="ci-top">
                  <button className="ci-name" onClick={() => setOpen((o) => ({ ...o, [p.id]: !o[p.id] }))}
                    aria-expanded={showNote}>
                    {p.display_name}
                  </button>
                  <div className="ci-gave">{gaveLine(p)}</div>
                  {p.suppressed && <div className="ci-flag">Unsubscribed from the newsletter. A personal note is your call.</div>}
                  {!p.email && <div className="ci-flag">No working email on file.</div>}
                </div>
                {showNote && (
                  <>
                    <div className="ci-draftlbl">Draft. Change anything.</div>
                    <textarea
                      className="ci-note"
                      value={noteFor(p)}
                      rows={8}
                      onChange={(e) => setText((t) => ({ ...t, [p.id]: e.target.value }))}
                      aria-label={`Note to ${p.display_name}`}
                    />
                    <div className="acts">
                      {p.email && (
                        <a className="btn navy" href={gmailComposeUrl(p.email, noteFor(p))} target="_blank" rel="noreferrer">
                          Open in Gmail
                        </a>
                      )}
                      <button className="btn ghost" onClick={() => copy(p)}>Copy</button>
                      <button className="btn go" disabled={busy === p.id} onClick={() => sent(p)}>I sent it</button>
                    </div>
                  </>
                )}
              </li>
            );
          })}
        </ol>
      )}

      {mode !== "closed" && total > people.length && (
        <div className="ci-more">Showing the newest {people.length} of {total}. Send these and the next ones move up.</div>
      )}
      {mode !== "closed" && (
        <div className="ci-more">
          Based on contact recorded in the database. Mail sent outside this page isn't counted yet.
        </div>
      )}
    </section>
  );
}
